import {
  CompleteUploadReq,
  DownloadTokenResponse,
  FsRawNode,
  GetPresignedUrlReq,
  PresignedUrlResponse,
  StorageStats,
  UploadLargeFileReq,
  UploadStorageFilesReq,
  UploadStorageFilesResponse,
} from '~/api/types';
import { ENV } from '~/config';
import { RootState } from '~/redux';
import { findFolderByPath, validateFilePath } from '~/utils';
import { v2Api } from '../v2Api';

const tags = ['storage'] as const;
const rootApiUrl = ENV.VITE_ROOT_API_URL.replace(/\/+$/, '');

const storageApi = v2Api
  .enhanceEndpoints({
    addTagTypes: tags,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getStorage: builder.query<FsRawNode[], object>({
        query: () => 'users/me/storage',
        transformResponse: (response: { data: FsRawNode[] }) => response.data,
        providesTags: tags,
      }),
      createFolder: builder.mutation<object, { folder_path: string }>({
        query: (body) => {
          return {
            url: 'users/me/storage/folders',
            method: 'POST',
            body,
          };
        },
        invalidatesTags: tags,
      }),
      renameFolder: builder.mutation<object, { current_path: string; new_path: string }>({
        query: (body) => {
          return {
            url: 'users/me/storage/folders/rename',
            method: 'PATCH',
            body,
          };
        },
        invalidatesTags: tags,
      }),
      deleteFolder: builder.mutation<object, { folder_path: string }>({
        query: (body) => {
          return {
            url: 'users/me/storage/folders',
            method: 'DELETE',
            body,
          };
        },
        invalidatesTags: tags,
      }),
      deleteFiles: builder.mutation<object, { file_path_list: string[] }>({
        query: (body) => {
          return {
            url: 'users/me/storage/files',
            method: 'DELETE',
            body,
          };
        },
        // comments this line to support deleting more than 10 files without unneccessary GET refetch
        // invalidatesTags: tags,
      }),
      renameFile: builder.mutation<object, { current_path: string; new_path: string }>({
        query: (body) => {
          return {
            url: 'users/me/storage/files/move',
            method: 'PATCH',
            body,
          };
        },
        invalidatesTags: tags,
      }),
      uploadStorageFiles: builder.mutation<UploadStorageFilesResponse, UploadStorageFilesReq>({
        queryFn: async (filesConfig: UploadStorageFilesReq, api) => {
          const state = api.getState() as RootState;
          const token = state.userSession.idToken;
          const { files, folder_path, onProgress } = filesConfig;

          const data = await new Promise<UploadStorageFilesResponse>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
              if (event.lengthComputable) {
                const raw = Math.round((event.loaded / event.total) * 100);
                const percent = raw >= 100 ? 99 : raw;
                onProgress(percent);
              }
            });

            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                onProgress(100);
                resolve(JSON.parse(xhr.response));
              } else {
                reject(xhr.response);
              }
            };

            xhr.onerror = () => {
              reject(xhr.response);
            };

            const body = new FormData();
            body.append('folder_path', folder_path);
            for (const file of files) {
              body.append('files', file);
            }

            xhr.open('POST', `${rootApiUrl}/users/me/storage/files/upload`, true);
            if (token) {
              xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            }

            xhr.send(body);
          });

          return { data };
        },
        onQueryStarted: async ({ folder_path, files }, { dispatch }) => {
          dispatch(
            storageApi.util.updateQueryData('getStorage', {}, (draft) => {
              const newFiles = files.map((file) => {
                const path = `${folder_path}/${file.name}`;

                return {
                  children: [],
                  is_dir: false,
                  last_modified: new Date().toISOString(),
                  name: file.name,
                  path: path,
                  size: file.size,
                };
              });

              const folderNode = folder_path === '' ? draft : findFolderByPath(draft, folder_path, false)?.children;
              if (folderNode) {
                const filteredFiles = newFiles.filter((file) => !folderNode.some((child) => child.name === file.name));

                if (filteredFiles.length > 0) {
                  folderNode.unshift(...filteredFiles);
                }
              } else {
                console.error('Folder not found in tree:', folder_path);
              }
            }),
          );
        },
        // comments this line to support uploading more than 10 files without unneccessary GET refetch
        // invalidatesTags: tags,
      }),
      uploadLargeFile: builder.mutation<PresignedUrlResponse, UploadLargeFileReq>({
        queryFn: async (fileConfig: UploadLargeFileReq) => {
          const { file, presignedUrl, onProgress } = fileConfig;

          try {
            await new Promise<void>((resolve, reject) => {
              const xhr = new XMLHttpRequest();

              xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                  const progress = Math.round((event.loaded / event.total) * 100);
                  onProgress(progress >= 100 ? 99 : progress);
                }
              });

              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                  resolve();
                } else {
                  reject(new Error(`Upload failed with status ${xhr.status}`));
                }
              };

              xhr.onerror = () => reject(new Error('Upload failed'));

              xhr.open('PUT', presignedUrl);
              xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
              xhr.send(file);
            });

            onProgress(100);

            return {
              data: { data: { url: presignedUrl }, method: 'PUT', expires_in: 0, content_type: file.type || null },
            };
          } catch (error) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: error instanceof Error ? error.message : 'Upload failed',
              },
            };
          }
        },
        onQueryStarted: async ({ file, folder_path }, { dispatch }) => {
          dispatch(
            storageApi.util.updateQueryData('getStorage', {}, (draft) => {
              const path = `${folder_path}/${file.name}`;
              const newFile: FsRawNode = {
                children: [],
                is_dir: false,
                last_modified: new Date().toISOString(),
                name: file.name,
                path: path,
                size: file.size,
              };
              const folderNode = folder_path === '' ? draft : findFolderByPath(draft, folder_path, false)?.children;
              if (folderNode) {
                const fileExists = folderNode.some((child) => child.name === file.name);
                if (!fileExists) {
                  folderNode.unshift(newFile);
                }
              } else {
                console.error('Folder not found in tree:', folder_path);
              }
            }),
          );
        },
      }),
      getStorageStats: builder.query<StorageStats, object>({
        query: () => 'users/me/storage/stats',
        transformResponse: (response: { data: StorageStats }) => response.data,
        providesTags: tags,
      }),
      getPresignedUploadUrl: builder.mutation<PresignedUrlResponse, GetPresignedUrlReq>({
        query: (body) => ({
          url: 'users/me/storage/files/presigned-url',
          method: 'POST',
          body,
        }),
        transformResponse: (response: { data: PresignedUrlResponse }) => response.data,
      }),
      completeS3Upload: builder.mutation<FsRawNode, CompleteUploadReq>({
        query: (body) => ({
          url: 'users/me/storage/files/complete',
          method: 'POST',
          body,
        }),
        transformResponse: (response: { data: FsRawNode }) => response.data,
        invalidatesTags: tags,
      }),
      getDownloadToken: builder.mutation<DownloadTokenResponse, { filePath: string }>({
        query: ({ filePath }) => ({
          url: `users/me/storage/files/download-token?file_path=${encodeURIComponent(filePath)}`,
          method: 'POST',
        }),
        transformResponse: (response: { data: DownloadTokenResponse }) => response.data,
      }),
      downloadFile: builder.mutation<void, { filePath: string; fileName: string }>({
        queryFn: async ({ filePath, fileName }, api, _extraOptions, baseQuery) => {
          if (!validateFilePath(filePath)) {
            console.error('[Download] Invalid file path provided:', filePath);

            return { error: { status: 400, data: 'Invalid file path provided' } };
          }
          if (!fileName || fileName.trim().length === 0 || fileName.length > 255) {
            return { error: { status: 400, data: 'Invalid file name provided' } };
          }

          try {
            const tokenResponse = await baseQuery({
              url: `users/me/storage/files/download-token?file_path=${encodeURIComponent(filePath)}`,
              method: 'POST',
            });

            if (tokenResponse.error || !tokenResponse.data) {
              return { error: { status: 500, data: 'Failed to get download token' } };
            }

            const { token } = (tokenResponse.data as { data: DownloadTokenResponse }).data;

            const downloadUrl = `${rootApiUrl}/users/me/storage/files/download-with-token?token=${encodeURIComponent(token)}`;

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
              document.body.removeChild(link);
            }, 100);

            return { data: undefined };
          } catch (error) {
            console.error('[Download] Exception during download:', error);

            return { error: { status: 500, data: 'Failed to download file' } };
          }
        },
      }),
    }),
  });

export const { useDownloadFileMutation } = storageApi;

export default storageApi;
